import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import useSEO, { SEO_CONFIG } from '@/hooks/useSEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TapRental() {
  // SEO optimization for tap rental page
  useSEO(SEO_CONFIG.tapRental);
  
  const [formData, setFormData] = useState({
    event_date: '',
    location: '',
    number_of_people: '',
    beers_preferences: '',
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/tap-rental`, {
        ...formData,
        number_of_people: parseInt(formData.number_of_people),
      });
      toast.success('Demande envoyée ! Nous vous contacterons très prochainement.');
      setFormData({
        event_date: '',
        location: '',
        number_of_people: '',
        beers_preferences: '',
        name: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      toast.error('Erreur lors de l’envoi de la demande');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-24 lg:py-32">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-5xl lg:text-6xl font-heading text-[#1A1D1F] mb-8 text-center">
          Location de tireuse à bière à Saint-Rémy-de-Provence
        </h1>
        <p className="text-lg text-foreground/70 text-center mb-8">
          Louez notre tireuse à bière professionnelle pour vos événements dans les Alpilles : mariages, anniversaires, associations, événements professionnels.
        </p>
        <p className="text-center text-foreground/70 mb-8">
          Servez une <a href="/nos-bieres" className="text-primary font-medium hover:underline">bière artisanale locale</a> en pression lors de votre événement. Vous êtes un professionnel ? Découvrez notre <a href="/professionnels" className="text-primary font-medium hover:underline">offre dédiée aux établissements</a>.
        </p>

        <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 mb-12 text-foreground/80">
          <div className="flex items-center">
            <span className="text-accent mr-2 text-lg">✓</span>
            <span>Installation simple et rapide</span>
          </div>
          <div className="flex items-center">
            <span className="text-accent mr-2 text-lg">✓</span>
            <span>Matériel fiable et professionnel</span>
          </div>
          <div className="flex items-center">
            <span className="text-accent mr-2 text-lg">✓</span>
            <span>Fûts de bière artisanale locale</span>
          </div>
        </div>

        <div className="mb-12 rounded-2xl overflow-hidden shadow-xl">
          <img
            src="https://customer-assets.emergentagent.com/job_craft-beer-shop-1/artifacts/x8yagodb_PXL_20250305_104046804.jpg"
            alt="Location tireuse à bière Saint-Rémy-de-Provence - Soif de Provence"
            className="w-full h-96 object-cover"
          />
        </div>

        <div className="bg-secondary/50 p-8 rounded-2xl border border-stone-200 mb-12">
          <h2 className="text-2xl font-heading text-foreground mb-4">Conditions de location de tireuse</h2>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-accent mr-2 text-xl">✓</span>
              <span><strong>Tireuse</strong> mise à disposition selon le volume commandé</span>
            </li>
            <li className="flex items-start">
              <span className="text-accent mr-2 text-xl">✓</span>
              <span><strong>Fûts de 30 litres</strong> de bière artisanale locale</span>
            </li>
            <li className="flex items-start">
              <span className="text-accent mr-2 text-xl">✓</span>
              <span>Livraison et installation possibles (sur devis)</span>
            </li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="tap-rental-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="event_date">Date de l'événement *</Label>
              <Input
                id="event_date"
                type="date"
                value={formData.event_date}
                onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                required
                data-testid="event-date-input"
              />
            </div>
            <div>
              <Label htmlFor="location">Lieu *</Label>
              <Input
                id="location"
                type="text"
                placeholder="Ville ou adresse"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
                data-testid="location-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="number_of_people">Nombre de personnes *</Label>
              <Input
                id="number_of_people"
                type="number"
                min="1"
                placeholder="50"
                value={formData.number_of_people}
                onChange={(e) => setFormData({ ...formData, number_of_people: e.target.value })}
                required
                data-testid="number-of-people-input"
              />
            </div>
            <div>
              <Label htmlFor="beers_preferences">Préférences de bières *</Label>
              <Input
                id="beers_preferences"
                type="text"
                placeholder="Blonde, IPA, Ambrée..."
                value={formData.beers_preferences}
                onChange={(e) => setFormData({ ...formData, beers_preferences: e.target.value })}
                required
                data-testid="beers-preferences-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label htmlFor="name">Nom complet *</Label>
              <Input
                id="name"
                type="text"
                placeholder="Jean Dupont"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="name-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="jean@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="email-input"
              />
            </div>
            <div>
              <Label htmlFor="phone">Téléphone *</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="06 XX XX XX XX"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                data-testid="phone-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="message">Message (optionnel)</Label>
            <Textarea
              id="message"
              placeholder="Informations complémentaires..."
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={4}
              data-testid="message-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-accent text-accent-foreground hover:bg-accent/90 hover:shadow-lg rounded-full py-6 text-lg font-semibold transition-all"
            disabled={submitting}
            data-testid="submit-tap-rental-button"
          >
            {submitting ? 'Envoi en cours...' : 'Demander un devis'}
          </Button>
        </form>
      </div>
    </div>
  );
}